import {
  ArrowUpOutlined,
  ReloadOutlined,
  RocketOutlined,
  HourglassOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
  SyncOutlined,
  DatabaseOutlined,
  BookOutlined,
  CloseOutlined,
  DownOutlined,
  UpOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import {
  Affix,
  Button,
  Drawer,
  Empty,
  Input,
  message,
  notification,
  Select,
  Spin,
  Table,
  Tooltip,
  Modal,
  DatePicker,
  Card,
  Progress,
  Alert,
} from "antd";
import DashboardHeader from "components/Dashboard/Header";
import DashboardPageChrome from "components/Dashboard/DashboardPageChrome";
import NestedTabs from "components/NestedTabs";
import MLDropdown from "helperComponents/MLDropdown";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router-dom";
import * as actions from "redux/Dashboard/actions.js";
import { apiRequest } from "../../util/services";
import { fetchAiInsightsQuota } from "util/aiInsightsQuota";
import AIInsightsQuotaPanel from "components/Dashboard/AIInsightsQuotaPanel";
import WelcomeDrawerReviewsList from "pages/Welcome/WelcomeDrawerReviewsList";
import styles from "./style.module.scss";
import dayjs from "dayjs";
import "dayjs/locale/tr";
const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;
const { confirm } = Modal;

/** Main app top bar height — Affix offset so toolbar sits flush under navbar */
const DASHBOARD_TOP_NAV_OFFSET = 64;
/** Vertical scroll area for AI insights table body (sticky header needs bounded height) */
const AI_INSIGHTS_TABLE_BODY_MAX_HEIGHT = "calc(100vh - 272px)";

const STATUS_OPTIONS = [
  {
    value: "planning",
    label: "Planning to take action",
    requiresComment: false,
    textLabel: "What action are you planning to take?",
    placeholder: "Describe your planned action…",
  },
  {
    value: "completed",
    label: "Action taken",
    requiresComment: true,
    textLabel: "Briefly describe the action you took",
    placeholder: "Describe your completed action…",
  },
  {
    value: "no_action",
    label: "No action needed",
    requiresComment: false,
    textLabel: "Briefly explain why no action is needed",
    placeholder: "Explain why no action is required…",
  },
];

const formatDatePeriod = (value) => {
  if (!value) return "—";

  const text = String(value);
  const match = text.match(
    /Since:\s*([0-9]{4}-[0-9]{2}-[0-9]{2}).*Until:\s*([0-9]{4}-[0-9]{2}-[0-9]{2})/i
  );
  if (!match) return text;

  const since = dayjs(match[1]).locale("tr");
  const until = dayjs(match[2]).locale("tr");
  if (!since.isValid() || !until.isValid()) return text;

  if (since.year() === until.year()) {
    return `${since.format("D MMM")} - ${until.format("D MMM YYYY")}`;
  }
  return `${since.format("D MMM YYYY")} - ${until.format("D MMM YYYY")}`;
};

const formatInlineJobDetails = ({ since, until, pivotFilter }) => {
  const parts = [];

  if (pivotFilter?.column && pivotFilter?.value) {
    parts.push(`${pivotFilter.column}: ${pivotFilter.value}`);
  }

  if (since && until) {
    const sinceDate = dayjs(since);
    const untilDate = dayjs(until);

    if (sinceDate.isValid() && untilDate.isValid()) {
      if (sinceDate.year() === untilDate.year()) {
        parts.push(
          `${sinceDate.format("D MMM")} - ${untilDate.format("D MMM YYYY")}`
        );
      } else {
        parts.push(
          `${sinceDate.format("D MMM YYYY")} - ${untilDate.format(
            "D MMM YYYY"
          )}`
        );
      }
    }
  }

  return parts.join(" | ");
};

// Job status component for showing generation progress
export const JobStatusCard = ({
  jobStatus,
  onRefresh,
  onCancel,
  compact = false,
  description,
  detailText,
}) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 0:
        return "#1890ff";
      case 2:
        return "#52c41a";
      case 3:
        return "#ff4d4f";
      case 4:
        return "#faad14";
      default:
        return "#d9d9d9";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 0:
        return "Generation in Progress";
      case 2:
        return "Generation Completed";
      case 3:
        return "Generation Failed";
      case 4:
        return "Generation Cancelled";
      default:
        return "Unknown Status";
    }
  };

  return (
    <div className={compact ? styles.inlineJobStatusCard : styles.emptyTopInsights}>
      <div className={styles.jobStatusHeader}>
        <div className={styles.jobStatusIcon}>
          {jobStatus.status === 0 ? (
            <HourglassOutlined
              style={{
                fontSize: compact ? "18px" : "24px",
                color: getStatusColor(jobStatus.status),
              }}
            />
          ) : (
            <RocketOutlined
              style={{
                fontSize: compact ? "18px" : "24px",
                color: getStatusColor(jobStatus.status),
              }}
            />
          )}
        </div>
        <div className={styles.jobStatusInfo}>
          <h3>{getStatusText(jobStatus.status)}</h3>
          <p>
            {description ||
              "AI is analyzing your data to generate insights..."}
          </p>
          {detailText ? (
            <div className={styles.jobStatusDetail}>{detailText}</div>
          ) : null}
        </div>
      </div>

      {jobStatus.status === 0 && (
        compact ? (
          <div className={styles.inlineProgressSection}>
            <Progress
              percent={jobStatus.progress || 0}
              status="active"
              showInfo={false}
              strokeColor="#4400aa"
            />
            <span className={styles.inlineProgressText}>
              {jobStatus.progress || 0}% complete
            </span>
          </div>
        ) : (
          <div className={styles.progressSection}>
            <Progress
              percent={jobStatus.progress || 0}
              status="active"
              percentPosition={{ align: "center", type: "inner" }}
              size={[200, 20]}
              strokeColor="#4400aa"
              steps={8}
              trailColor="#6B7280"
              type="circle"
              format={(percent) => (
                <span className={styles.percentProg}>{percent}%</span>
              )}
            />
          </div>
        )
      )}

      {jobStatus.status === "failed" && (
        <Alert
          message="Generation Failed"
          description={
            jobStatus.error_message ||
            "An error occurred during generation. Please try again."
          }
          type="error"
          showIcon
          className={styles.errorAlert}
        />
      )}

      <div className={styles.jobActions}>
        <Button
          icon={<ReloadOutlined />}
          onClick={onRefresh}
          loading={jobStatus.refreshing}
          size="large"
          className={styles.primaryAction}
        >
          Refresh Status
        </Button>
      </div>

      {jobStatus.estimated_completion && (
        <div className={styles.estimatedTime}>
          <small>Estimated completion: {jobStatus.estimated_completion}</small>
        </div>
      )}
    </div>
  );
};

// Enhanced empty state component for AI insights
export const EmptyTopInsights = ({
  onGenerate,
  isGenerating = false,
  generationProgress = 0,
  estimatedTimeRemaining = null,
  onConnectData,
  onLearnMore,
}) => {
  const renderLoadingState = () => (
    <div className={styles.loadingState}>
      <div className={styles.loadingIcon}>
        <Spin
          size="large"
          indicator={
            <SyncOutlined style={{ fontSize: 48, color: "#1890ff" }} spin />
          }
        />
      </div>
      <h3 className={styles.loadingTitle}>Generating AI Insights</h3>
      <p className={styles.loadingDescription}>
        Our AI is analyzing your data to uncover <b>root causes</b> and generate
        <b> actionable recommendations</b>. This may take a few minutes.
      </p>

      {generationProgress > 0 && (
        <div className={styles.progressContainer}>
          <Progress
            percent={generationProgress}
            showInfo={false}
            status="active"
            percentPosition={{ align: "center", type: "inner" }}
            size={[200, 20]}
            strokeColor="#4400aa"
          />
          <div className={styles.progressDetails}>
            <span className={styles.progressText}>
              {generationProgress}% complete
            </span>
            {estimatedTimeRemaining && (
              <span className={styles.timeRemaining}>
                ~{estimatedTimeRemaining} remaining
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const renderEmptyState = () => (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description={null}
      className={styles.emptyContainer}
    >
      <div className={styles.emptyContent}>
        <h3 className={styles.emptyTitle}>No AI Insights Yet</h3>
        <p className={styles.emptyDescription}>
          AI Insights help you uncover the <b>root causes</b> behind trends and
          provide <b>data-driven recommendations</b>.
        </p>

        <div className={styles.emptyActions}>
          <Button
            icon={<RocketOutlined />}
            onClick={onGenerate}
            size="large"
            className={styles.primaryAction}
          >
            Generate AI Insights
          </Button>
        </div>

        <div className={styles.helpText}>
          <p className={styles.helpDescription}>
            Need help?{" "}
            <a href="#" className={styles.helpLink}>
              View our guide
            </a>{" "}
            on getting the most out of AI Insights.
          </p>
        </div>
      </div>
    </Empty>
  );

  return (
    <div
      className={`${styles.emptyTopInsights} ${
        isGenerating ? styles.generating : ""
      }`}
    >
      {isGenerating ? renderLoadingState() : renderEmptyState()}
    </div>
  );
};

// Helper function outside component to avoid re-creation and initialization issues
const getDefaultDateRange = (rangeId) => {
  const today = dayjs();
  let startDate, endDate;
  
  switch (rangeId) {
    case "last24":
      startDate = today.subtract(24, "hour");
      endDate = today;
      break;
    case "last7d":
      startDate = today.subtract(6, "day");
      endDate = today;
      break;
    case "last14d":
      startDate = today.subtract(13, "day");
      endDate = today;
      break;
    case "last30d":
      startDate = today.subtract(29, "day");
      endDate = today;
      break;
    case "last90d":
      startDate = today.subtract(89, "day");
      endDate = today;
      break;
    case "allTime":
      // For "all time", use a very old date (2000-01-01) to today
      startDate = dayjs("2000-01-01");
      endDate = today;
      break;
    case "monthToDate":
      startDate = today.startOf("month");
      endDate = today;
      break;
    default:
      startDate = today.subtract(6, "day");
      endDate = today;
  }
  
  return [
    startDate.format("YYYY-MM-DD"),
    endDate.format("YYYY-MM-DD")
  ];
};

// AI Insights Generation Modal - Fixed Version
export const AIInsightsModal = ({
  visible,
  onClose,
  onSubmit,
  topics = [],
  loading = false,
  dashboardId,
  pivotFilter = null,
  pivotOptions = null,
  isDark = false,
}) => {
  const [formLoading, setFormLoading] = useState(false);
  const [daysRange, setDaysRange] = useState("last7d");
  const [reportDateRange, setReportDateRange] = useState(() =>
    getDefaultDateRange("last7d")
  );
  const [selectedPivotColumn, setSelectedPivotColumn] = useState("");
  const [selectedPivotValue, setSelectedPivotValue] = useState("");
  const [segmentExpanded, setSegmentExpanded] = useState(false);
  const [isProcessingState, setIsProcessingState] = useState(false);
  const [aiQuota, setAiQuota] = useState(null);
  const [aiQuotaLoading, setAiQuotaLoading] = useState(false);
  const [aiQuotaError, setAiQuotaError] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (visible) {
      setReportDateRange(getDefaultDateRange("last7d"));
      setDaysRange("last7d");
      setSelectedPivotColumn("");
      setSelectedPivotValue("");
      setSegmentExpanded(false);
      setIsProcessingState(false);
      setAiQuota(null);
      setAiQuotaError(false);
      setAiQuotaLoading(false);
    }
  }, [visible, pivotFilter]);

  const handlePresetSelect = (rangeId) => {
    setDaysRange(rangeId);
    setReportDateRange(getDefaultDateRange(rangeId));
  };

  const handleSubmit = async () => {
    const payload = {
      dashboard_id: dashboardId,
      since: reportDateRange[0],
      until: reportDateRange[1],
    };
    if (selectedPivotColumn && selectedPivotValue) {
      payload.pivotFilter = {
        column: selectedPivotColumn,
        value: selectedPivotValue,
      };
    }

    setFormLoading(true);
    try {
      const started = await onSubmit(payload);
      if (started) {
        setIsProcessingState(true);
      }
    } finally {
      setFormLoading(false);
    }
  };

  const pivotColumns = Object.keys(pivotOptions || {});
  const pivotValues = selectedPivotColumn
    ? (pivotOptions?.[selectedPivotColumn] || []).map((v) => String(v))
    : [];
  const presetOptions = [
    { id: "last7d", label: t("filter.16") },
    { id: "last30d", label: t("filter.18") },
    { id: "monthToDate", label: t("ai_insights_modal.thisMonth") },
  ];

  const rangePickerValue = [
    reportDateRange?.[0] ? dayjs(reportDateRange[0]) : null,
    reportDateRange?.[1] ? dayjs(reportDateRange[1]) : null,
  ];

  useEffect(() => {
    if (!visible || !dashboardId) {
      return;
    }
    const start = reportDateRange?.[0];
    const end = reportDateRange?.[1];
    if (!start || !end) {
      return;
    }
    let cancelled = false;
    (async () => {
      setAiQuotaLoading(true);
      setAiQuotaError(false);
      setAiQuota(null);
      try {
        const res = await fetchAiInsightsQuota(apiRequest, {
          dashboardId,
          startDate: start,
          endDate: end,
        });
        if (cancelled) {
          return;
        }
        if (res?.error || res?.validation_error) {
          console.warn("[WARN] TopProblems AI insights quota check returned error", res);
          setAiQuotaError(true);
        } else {
          setAiQuota(res);
        }
      } catch (e) {
        console.error("[ERROR] TopProblems AI insights quota fetch failed", e);
        if (!cancelled) {
          setAiQuotaError(true);
        }
      } finally {
        if (!cancelled) {
          setAiQuotaLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    visible,
    dashboardId,
    reportDateRange[0],
    reportDateRange[1],
  ]);

  const quotaThisRun =
    aiQuota?.required_amount ?? aiQuota?.total_review_count ?? null;
  const quotaBlocked =
    aiQuota &&
    aiQuota.unlimited !== true &&
    typeof aiQuota.current_quota === "number" &&
    quotaThisRun != null &&
    quotaThisRun > aiQuota.current_quota;

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      closable={false}
      width={560}
      destroyOnClose
      zIndex={1050}
      maskClosable={false}
      className={styles.aiInsightsModal}
    >
      <div className={styles.aiInsightsBody}>
        <div className={styles.aiInsightsHeader}>
          <div>
            <div className={styles.aiInsightsTitle}>
              {t("ai_insights_modal.title")}
            </div>
            <div className={styles.aiInsightsSubtitle}>
              {t("ai_insights_modal.subtitle")}
            </div>
          </div>
          <button
            type="button"
            className={styles.aiInsightsClose}
            onClick={onClose}
            aria-label={t("common.cancel")}
          >
            <CloseOutlined />
          </button>
        </div>

        {!isProcessingState ? (
          <>
            <div className={styles.aiInsightsContent}>
              <div className={styles.aiFormSection}>
                <label className={styles.aiFormLabel}>
                  {t("ai_insights_modal.dateRange")}
                </label>
                <RangePicker
                  value={rangePickerValue}
                  className={styles.aiRangePicker}
                  format="DD MMM YYYY"
                  allowClear={false}
                  onChange={(values) => {
                    if (values?.[0] && values?.[1]) {
                      setReportDateRange([
                        values[0].format("YYYY-MM-DD"),
                        values[1].format("YYYY-MM-DD"),
                      ]);
                      setDaysRange("custom");
                    }
                  }}
                />
                <div className={styles.aiPresetRow}>
                  {presetOptions.map((preset) => (
                    <button
                      key={preset.id}
                      type="button"
                      className={`${styles.aiPresetChip} ${
                        daysRange === preset.id ? styles.activePresetChip : ""
                      }`}
                      onClick={() => handlePresetSelect(preset.id)}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className={styles.aiSegmentSection}>
                <button
                  type="button"
                  className={styles.aiSegmentToggle}
                  onClick={() => setSegmentExpanded((prev) => !prev)}
                >
                  <span>{t("ai_insights_modal.segmentOptional")}</span>
                  {segmentExpanded ? <UpOutlined /> : <DownOutlined />}
                </button>

                {segmentExpanded && (
                  <div className={styles.aiSegmentFields}>
                    <div className={styles.aiFieldGroup}>
                      <label className={styles.aiFormLabel}>
                        {t("ai_insights_modal.segmentField")}
                      </label>
                      <Select
                        className={styles.aiSelect}
                        placeholder={t("ai_insights_modal.selectSegmentField")}
                        value={selectedPivotColumn || undefined}
                        onChange={(value) => {
                          setSelectedPivotColumn(value);
                          setSelectedPivotValue("");
                        }}
                        allowClear
                        onClear={() => {
                          setSelectedPivotColumn("");
                          setSelectedPivotValue("");
                        }}
                      >
                        {pivotColumns.map((col) => (
                          <Option key={col} value={col}>
                            {col}
                          </Option>
                        ))}
                      </Select>
                    </div>
                    <div className={styles.aiFieldGroup}>
                      <label className={styles.aiFormLabel}>
                        {t("ai_insights_modal.value")}
                      </label>
                      <Select
                        className={styles.aiSelect}
                        placeholder={t("ai_insights_modal.all")}
                        value={selectedPivotValue || undefined}
                        onChange={(value) => setSelectedPivotValue(value)}
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          String(option?.children || "")
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                        allowClear
                        disabled={!selectedPivotColumn}
                        onClear={() => setSelectedPivotValue("")}
                      >
                        <Option value="">{t("ai_insights_modal.all")}</Option>
                        {pivotValues.map((val) => (
                          <Option key={val} value={val}>
                            {val}
                          </Option>
                        ))}
                      </Select>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className={styles.aiInsightsQuotaWrap}>
              <AIInsightsQuotaPanel
                quota={aiQuota}
                loading={aiQuotaLoading}
                error={aiQuotaError}
                isDark={isDark}
              />
            </div>

            <div className={`${styles.aiInsightsFooter} ${isDark ? styles.aiInsightsFooterDark : ""}`}>
              <div className={styles.aiInsightsHint}>
                <div>{t("ai_insights_modal.durationHint")}</div>
                <div className={styles.aiInsightsQuotaFootnote}>
                  {t("common.aiInsightsFooterQuotaNote")}
                </div>
              </div>
              <div className={styles.aiInsightsActions}>
                <Button onClick={onClose} disabled={formLoading}>
                  {t("button.3")}
                </Button>
                <Button
                  type="primary"
                  onClick={handleSubmit}
                  loading={formLoading}
                  disabled={quotaBlocked}
                  icon={<RocketOutlined />}
                  className={styles.aiInsightsPrimary}
                >
                  {t("ai_insights_modal.generate")}
                </Button>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className={styles.aiInsightsProcessing}>
              <Spin size="large" />
              <div className={styles.aiInsightsProcessingTitle}>
                {t("ai_insights_modal.processingTitle")}
              </div>
              <div className={styles.aiInsightsProcessingInfo}>
                {t("ai_insights_modal.processingInfo")}
              </div>
            </div>
            <div className={styles.aiInsightsFooter}>
              <div />
              <div className={styles.aiInsightsActions}>
                <Button onClick={onClose}>{t("button.3")}</Button>
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export const StatusUpdateComponent = ({
  record,
  onStatusUpdate,
  endpoint,
  persistMode = "api",
  onLocalPersist,
  getPopupContainer,
  /** Merged onto root wrapper; use e.g. { minWidth: 0 } in narrow tables (Welcome edit modal). */
  containerStyle,
  /** When value is unset, Select shows this instead of a real option label (e.g. Welcome metric 20). */
  placeholder,
  /** Shown when Save is clicked with no status selected; falls back to English if omitted. */
  statusRequiredError,
  /** Smaller Select + tighter layout (Welcome root-cause cards). */
  compact = false,
}) => {
  const { t } = useTranslation();
  const resolvedInitialStatus = record.status ?? record.Status;
  const [state, setState] = useState({
    selectedStatus: resolvedInitialStatus,
    comment: "",
    showCommentField: false,
    isSubmitting: false,
    validationError: "",
  });
  const containerRef = useRef(null);

  useEffect(() => {
    setState((prev) => ({
      ...prev,
      selectedStatus: record.status ?? record.Status,
      comment: record.comment || "",
      showCommentField: false,
      validationError: "",
    }));
  }, [record]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        if (!state.validationError && !state.isSubmitting) {
          handleCancel();
        }
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && state.showCommentField) {
        handleCancel();
      }
    };

    if (state.showCommentField) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    state.showCommentField,
    state.validationError,
    state.isSubmitting,
    state.comment,
    state.selectedStatus,
  ]);

  const handleStatusChange = useCallback((value) => {
    setState((prev) => ({
      ...prev,
      selectedStatus: value,
      showCommentField: true,
      validationError: "",
    }));
  }, []);

  const handleCommentChange = useCallback((e) => {
    setState((prev) => ({
      ...prev,
      comment: e.target.value,
      validationError: "",
    }));
  }, []);

  const handleSave = async () => {
    if (!state.selectedStatus) {
      console.warn(
        "[StatusUpdateComponent] WARNING save blocked: no status selected",
        { rc_id: record.rc_id ?? record.ID },
      );
      setState((prev) => ({
        ...prev,
        validationError:
          statusRequiredError || "Please select a status before saving.",
      }));
      return;
    }

    const statusConfig = STATUS_OPTIONS.find(
      (opt) => opt.value === state.selectedStatus
    );

    if (
      statusConfig?.requiresComment &&
      (!state.comment || state.comment.trim() === "")
    ) {
      setState((prev) => ({
        ...prev,
        validationError: "Please provide a short description before saving.",
      }));
      return;
    }

    if (persistMode === "local") {
      if (typeof onLocalPersist === "function") {
        const payload = {
          status: state.selectedStatus,
          comment: state.comment.trim(),
        };
        const maybePromise = onLocalPersist(payload);
        const finishOk = () => {
          setState((prev) => ({
            ...prev,
            showCommentField: false,
            validationError: "",
            isSubmitting: false,
          }));
        };
        if (maybePromise != null && typeof maybePromise.then === "function") {
          setState((prev) => ({ ...prev, isSubmitting: true }));
          try {
            await maybePromise;
            finishOk();
          } catch (err) {
            console.error("[StatusUpdateComponent] local persist failed", err);
            message.error(
              err?.message && String(err.message).trim()
                ? String(err.message)
                : t("top_problems.save_failed", "Save failed"),
            );
            setState((prev) => ({
              ...prev,
              isSubmitting: false,
              validationError:
                typeof err?.message === "string" && err.message.trim()
                  ? err.message
                  : t("top_problems.save_failed", "Save failed"),
            }));
          }
          return;
        }
        finishOk();
        return;
      }
      setState((prev) => ({
        ...prev,
        showCommentField: false,
        validationError: "",
        isSubmitting: false,
      }));
      return;
    }

    setState((prev) => ({ ...prev, isSubmitting: true }));

    try {
      const updateData = {
        rc_id: record.rc_id || record.ID,
        status: state.selectedStatus,
        comment: state.comment.trim(),
      };

      const response = await apiRequest(
        "dashboards/root-cause/status/update",
        "POST",
        updateData,
        true
      );

      if (response && response.success) {
        notification.success({
          message: t("top_problems.status_updated", "Status updated successfully!"),
          description: "",
        });
        setState((prev) => ({ ...prev, showCommentField: false }));
        onStatusUpdate();
      } else {
        notification.error({
          message: t("top_problems.status_update_failed", "Failed to update status"),
          description: "",
        });
      }
    } catch (error) {
      console.error("Error updating status:", error);
      message.error(t("top_problems.update_error", "Error updating status"));
    } finally {
      setState((prev) => ({ ...prev, isSubmitting: false }));
    }
  };

  const handleCancel = () => {
    setState({
      selectedStatus: record.status ?? record.Status,
      showCommentField: false,
      comment: record.comment || "",
      validationError: "",
      isSubmitting: false,
    });
  };

  const statusConfig = STATUS_OPTIONS.find(
    (opt) => opt.value === state.selectedStatus
  );

  return (
    <div
      ref={containerRef}
      style={{ minWidth: compact ? 0 : "200px", ...containerStyle }}
      className={`${styles.statusBox}${compact ? ` ${styles.statusBoxCompact}` : ""}`}
    >
      <Select
        key={`status-select-${record.rc_id}-${state.selectedStatus ?? "empty"}`}
        size={compact ? "small" : "middle"}
        value={state.selectedStatus}
        onChange={handleStatusChange}
        placeholder={placeholder ?? "Select status"}
        style={{
          width: "100%",
          marginBottom: state.showCommentField ? "8px" : "0",
        }}
        disabled={state.isSubmitting}
        getPopupContainer={
          getPopupContainer || ((trigger) => trigger.parentNode)
        }
        showSearch={false}
        optionFilterProp="children"
        dropdownClassName="status-update-dropdown"
      >
        {STATUS_OPTIONS.map((option) => (
          <Option key={option.value} value={option.value}>
            {option.label}
          </Option>
        ))}
      </Select>

      {state.showCommentField && (
        <div>
          <p style={{ fontSize: 12, margin: "4px 0" }}>
            {statusConfig?.textLabel || ""}
          </p>
          <TextArea
            value={state.comment}
            onChange={handleCommentChange}
            placeholder={statusConfig?.placeholder}
            rows={2}
            style={{
              marginBottom: "8px",
              borderColor: state.validationError ? "#ff4d4f" : undefined,
            }}
            disabled={state.isSubmitting}
            autoFocus
          />

          {state.validationError && (
            <div className={styles.statusValidationError}>
              {state.validationError}
            </div>
          )}

          <div
            style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}
          >
            <Button
              size="small"
              onClick={handleCancel}
              disabled={state.isSubmitting}
            >
              Cancel
            </Button>
            <Button
              size="small"
              type="primary"
              onClick={handleSave}
              loading={state.isSubmitting}
              disabled={
                state.selectedStatus === "completed" && !state.comment?.trim()
              }
            >
              Save
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const TopProblems = (props) => {
  const theme = useSelector((state) => state.DashboardReducer.theme);
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dashboardLoaded, setDashboardLoaded] = useState(false);
  const [likedItems, setLikedItems] = useState(new Set());
  const params = useParams();
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [issueReviews, setIssueReviews] = useState([]);

  // Custom pagination state
  const [startIndex, setStartIndex] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [selectedPerPage, setSelectedPerPage] = useState(10);
  const [numItems, setNumItems] = useState(0);
  const [pageChanged, setPageChanged] = useState(false);
  const [paginationInfo, setPaginationInfo] = useState(null);

  // New state for AI insights generation
  const [jobStatus, setJobStatus] = useState(null);
  const [showGenerationModal, setShowGenerationModal] = useState(false);
  const [topics, setTopics] = useState([]);
  const [pageState, setPageState] = useState("loading"); // 'loading', 'job_running', 'empty', 'has_data'
  const [generationLoading, setGenerationLoading] = useState(false);
  const [downloadInsightsLoading, setDownloadInsightsLoading] = useState(false);

  const pollingTimeoutRef = useRef(null);
  const isMountedRef = useRef(true);

  const searchParams = new URLSearchParams(location.search);
  const urlStartIndex = parseInt(searchParams.get("start_index") || "0", 10);

  // Pivot filter state (for MLDropdown)
  const [pivotOptions, setPivotOptions] = useState(null);
  const [generationPivotOptions, setGenerationPivotOptions] = useState(null);
  const [multiLevelOptions, setMultiLevelOptions] = useState(null);
  const [levelWisePivotSelections, setLevelWisePivotSelections] = useState({});
  const [selectedPivotsOperator, setSelectedPivotsOperator] = useState("AND");

  // Function to update URL parameters
  const updateQParams = (key, value) => {
    const params = new URLSearchParams(location.search);
    if (value !== null && value !== undefined && value !== "") {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    history.push({
      pathname: location.pathname,
      search: params.toString(),
    });
  };

  // Convert levelWisePivotSelections or URL to API pivot_filter: { column, value }
  const getPivotFilterForApi = () => {
    if (
      levelWisePivotSelections &&
      Object.keys(levelWisePivotSelections).length > 0
    ) {
      const firstCol = Object.keys(levelWisePivotSelections)[0];
      const colSelections = levelWisePivotSelections[firstCol];
      if (colSelections && typeof colSelections === "object") {
        const firstVal = Object.keys(colSelections)[0];
        if (firstVal) return { column: firstCol, value: firstVal };
      }
    }
    const pivotStr = new URLSearchParams(location.search).get("pivotFilter");
    if (pivotStr) {
      try {
        const pvt = JSON.parse(pivotStr);
        if (pvt && typeof pvt === "object") {
          const col = Object.keys(pvt)[0];
          const vals = pvt[col];
          const val = Array.isArray(vals) ? vals[0] : vals;
          if (col && val) return { column: col, value: String(val) };
        }
      } catch (_) {}
    }
    return null;
  };

  // Fetch pivot options for MLDropdown
  const fetchPivotOptions = async () => {
    const dashboardId = parseInt(params.id);
    if (!dashboardId) return;
    try {
      const res = await apiRequest(
        "dashboards/root-cause/pivot-options",
        "POST",
        { dashboard_id: dashboardId },
        true
      );
      if (res?.success && typeof res.success === "object") {
        const opts = res.success;
        if (Object.keys(opts).length > 0) {
          setPivotOptions(opts);
        }
      }
    } catch (e) {
      console.error("Error fetching pivot options:", e);
    }
  };

  // Fetch generation pivot options from DataAttributes cache
  const fetchGenerationPivotOptions = async () => {
    const dashboardId = parseInt(params.id, 10);
    if (!dashboardId) return;
    try {
      const res = await apiRequest(
        "dashboards/root-cause/pivot-options/generation",
        "POST",
        { dashboard_id: dashboardId },
        true
      );
      if (res?.success && typeof res.success === "object") {
        setGenerationPivotOptions(res.success);
      }
    } catch (e) {
      console.error("Error fetching generation pivot options:", e);
    }
  };

  // Convert pivotOptions to multiLevelOptions for MLDropdown
  useEffect(() => {
    if (pivotOptions && Object.keys(pivotOptions).length > 0) {
      const options = {
        level1: [],
        level2: {},
      };
      Object.keys(pivotOptions).forEach((key) => {
        options.level1.push({
          id: key,
          label: key,
          value: key,
        });
        options.level2[key] = (pivotOptions[key] || []).map((v) => ({
          id: String(v),
          label: String(v),
          value: String(v),
        }));
      });
      setMultiLevelOptions(options);
    }
  }, [pivotOptions]);

  // Sync pivot filter from URL on load
  useEffect(() => {
    const pivotFilterStr = searchParams.get("pivotFilter");
    if (
      pivotFilterStr &&
      multiLevelOptions &&
      Object.keys(multiLevelOptions?.level1 || {}).length > 0
    ) {
      try {
        const pvt = JSON.parse(pivotFilterStr);
        if (pvt && typeof pvt === "object") {
          const sel = {};
          Object.entries(pvt).forEach(([col, vals]) => {
            if (Array.isArray(vals) && vals.length > 0) {
              sel[col] = vals.reduce((acc, v) => {
                acc[String(v)] = {};
                return acc;
              }, {});
            } else if (vals) {
              sel[col] = { [String(vals)]: {} };
            }
          });
          if (Object.keys(sel).length > 0) {
            setLevelWisePivotSelections(sel);
          }
        }
      } catch (_) {}
    }
  }, [location.search, multiLevelOptions]);

  const handleApplyPivotFilters = () => {
    const pf = getPivotFilterForApi();
    if (pf) {
      updateQParams("pivotFilter", JSON.stringify({ [pf.column]: [pf.value] }));
    } else {
      updateQParams("pivotFilter", null);
    }
    updateQParams("start_index", "0");
    fetchData(0);
  };

  const handleResetPivotFilters = () => {
    updateQParams("pivotFilter", null);
    updateQParams("pivots_operator", null);
    setLevelWisePivotSelections({});
    updateQParams("start_index", "0");
    fetchData(0);
  };

  // Function to check job status
  const checkJobStatus = async () => {
    const dashboardId = parseInt(params.id);
    try {
      const response = await apiRequest(
        "dashboards/root-cause/job/status",
        "POST",
        { dashboard_id: dashboardId },
        true
      );
      if (response?.success?.job) {
        setJobStatus(response.success);
        return response.success;
      }
      return response?.success || null;
    } catch (error) {
      console.error("Error checking job status:", error);
      return null;
    }
  };

  // Function to start AI insights generation
  const startGeneration = async (generationParams) => {
    setGenerationLoading(true);
    try {
      const response = await apiRequest(
        "dashboards/gen-insights",
        "POST",
        generationParams,
        true
      );
      if (response?.error) {
        throw new Error(response.error);
      } else if (response?.success) {
        setJobStatus({
          status: 0,
          progress: 0,
          current_step: "Initializing...",
          job_id: response.success.job_id,
          has_insights: pageState === "has_data" && data.length > 0,
          message: "Job in progress",
        });
        setPageState(
          pageState === "has_data" && data.length > 0 ? "has_data" : "job_running"
        );
        setShowGenerationModal(false);
        notification.success({
          message: "AI Insights Generation Started",
          description:
            "Your AI insights are being generated. This may take several minutes.",
        });
        pollJobStatus();
        return true;
      } else {
        throw new Error("Failed to start generation");
      }
    } catch (error) {
      console.error("Error starting generation:", error);
      notification.error({
        message: "Generation Failed",
        description:
          error.message ||
          "Failed to start AI insights generation. Please try again.",
      });
      return false;
    } finally {
      setGenerationLoading(false);
    }
  };

  // Function to cancel job
  const cancelJob = async () => {
    if (!jobStatus?.job_id) return;

    setJobStatus((prev) => ({ ...prev, cancelling: true }));
    try {
      const response = await apiRequest(
        "dashboards/root-cause/job/cancel",
        "POST",
        { job_id: jobStatus.job_id },
        true
      );
      if (response?.success) {
        setJobStatus(null);
        setPageState("empty");
        notification.success({
          message: "Generation Cancelled",
          description: "AI insights generation has been cancelled.",
        });
      }
    } catch (error) {
      console.error("Error cancelling job:", error);
      notification.error({
        message: "Cancellation Failed",
        description: "Failed to cancel the generation job.",
      });
    } finally {
      setJobStatus((prev) => ({ ...prev, cancelling: false }));
    }
  };

  // Function to refresh job status
  const refreshJobStatus = async () => {
    setJobStatus((prev) => ({ ...prev, refreshing: true }));
    const status = await checkJobStatus();
    setJobStatus((prev) => ({ ...prev, refreshing: false }));

    if (status && !status.job && status.has_insights) {
      setPageState("has_data");
      fetchData(0);
      notification.success({
        message: "Generation Completed",
        description: "Your AI insights have been generated successfully!",
      });
    } else if (status && !status.job) {
      setPageState("empty");
      notification.error({
        message: "Generation Failed",
        description:
          status.message || "The generation process failed. Please try again.",
      });
    }
  };
  // Function to stop polling
  const stopPolling = useCallback(() => {
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
  }, []);
  // Function to poll job status
  const pollJobStatus = useCallback(async () => {
    // Check if component is still mounted
    if (!isMountedRef.current) {
      return;
    }

    const status = await checkJobStatus();
    if (status && isMountedRef.current) {
      if (!status.job && status.has_insights) {
        // Job completed - has insights
        setJobStatus(null);
        setPageState("has_data");
        fetchData(0);
        notification.success({
          message: "Generation Completed",
          description: "Your AI insights have been generated successfully!",
        });
        return;
      } else if (!status.job) {
        // Job completed/failed - no insights
        setJobStatus(null);
        setPageState("empty");
        return;
      }

      // Continue polling if job is still running and component is mounted
      //This part works but if we have a refresh button no need to dynamically pool status
      // if (status.job && status.status === 0 && isMountedRef.current) {
      //   pollingTimeoutRef.current = setTimeout(pollJobStatus, 5000);
      // }
    }
  }, []);
  // Cleanup effect - runs when component unmounts
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      // Component is unmounting
      isMountedRef.current = false;
      stopPolling();
    };
  }, [stopPolling]);

  // Also stop polling when navigating away
  useEffect(() => {
    const handleBeforeUnload = () => {
      stopPolling();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      stopPolling();
    };
  }, [stopPolling]);

  // Open generation modal directly (same experience as empty state)
  const handleRegenerate = () => {
    setShowGenerationModal(true);
  };

  const handleDownloadAIInsights = async () => {
    const dashboardId = parseInt(params.id, 10);
    if (!dashboardId) {
      notification.error({
        message: "Dashboard ID bulunamadı.",
      });
      return;
    }

    setDownloadInsightsLoading(true);
    try {
      const payload = {
        dashboard_id: dashboardId,
      };
      const since = searchParams.get("since");
      const until = searchParams.get("until");
      const pivotFilter = getPivotFilterForApi();

      if (since) payload.since = since;
      if (until) payload.until = until;
      if (pivotFilter) payload.pivot_filter = pivotFilter;

      const result = await apiRequest(
        "dashboards/root-cause/download",
        "POST",
        payload,
        true
      );

      if (!result?.error) {
        notification.success({
          message: (
            <span>
              AI Insights export kuyruğa alındı.
              <br />
              Go to{" "}
              <a
                href="/console/report?page=download"
                onClick={(e) => {
                  e.preventDefault();
                  history.push("/console/report?page=download");
                }}
              >
                Exports page
              </a>
            </span>
          ),
        });
      } else {
        notification.error({
          message: result.error || "Download failed",
        });
      }
    } catch (error) {
      notification.error({
        message: "Download failed",
      });
    } finally {
      setDownloadInsightsLoading(false);
    }
  };

  // Function to fetch reviews for a root cause
  const fetchReviewsForIssue = async (issue) => {
    setReviewsLoading(true);
    setSelectedIssue(issue);
    setDrawerOpen(true);
    try {
      const payload = { rc_id: issue.rc_id };
      const response = await apiRequest(
        "dashboards/root-cause/reviews",
        "POST",
        payload,
        true
      );
      if (response?.success) {
        setIssueReviews(response.success);
      } else {
        setIssueReviews([]);
      }
    } catch (error) {
      setIssueReviews([]);
    } finally {
      setReviewsLoading(false);
    }
  };

  // Function to fetch data with pagination
  const fetchData = async (index) => {
    const dashboardId = parseInt(params.id);

    const payload = {
      dashboard_id: dashboardId,
      start_index: index,
      num_items: selectedPerPage,
    };
    const pivotFilter = getPivotFilterForApi();
    if (pivotFilter) {
      payload.pivot_filter = pivotFilter;
    }

    setLoading(true);

    try {
      const res = await apiRequest(
        "dashboards/root-cause",
        "POST",
        payload,
        true
      );
      if (res?.success) {
        // Handle new API response structure
        if (res.success.data && res.success.pagination) {
          // New paginated response
          setData(res.success.data);
          setNumItems(res.success.pagination.total);
          setPaginationInfo(res.success.pagination);

          if (res.success.data.length > 0) {
            setPageState("has_data");
          }
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      message.error(t("top_problems.fetch_error", "Failed to fetch data. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  // Initial page state determination
  const determineInitialState = async () => {
    setLoading(true);
    const dashboardId = parseInt(params.id);

    try {
      // Check if there's a running job first
      const jobStatus = await checkJobStatus();
      if (!isMountedRef.current) return; // ADD THIS LINE

      if (jobStatus && jobStatus.job && jobStatus.status === 0) {
        setJobStatus(jobStatus);

        if (jobStatus.has_insights) {
          const payload = {
            dashboard_id: dashboardId,
            start_index: 0,
            num_items: selectedPerPage,
          };
          const pivotFilter = getPivotFilterForApi();
          if (pivotFilter) {
            payload.pivot_filter = pivotFilter;
          }

          const res = await apiRequest(
            "dashboards/root-cause",
            "POST",
            payload,
            true
          );

          if (res?.success && res.success.data && res.success.data.length > 0) {
            setData(res.success.data);
            setNumItems(
              res.success.pagination?.total || res.success.data.length
            );
            setPaginationInfo(res.success.pagination);
            setPageState("has_data");
            pollJobStatus();
            return;
          }
        }

        setPageState("job_running");
        pollJobStatus(); // Start polling
        return;
      }

      // Check if insights exist even without a running job
      if (jobStatus && jobStatus.has_insights) {
        // Fetch existing data
        const payload = {
          dashboard_id: dashboardId,
          start_index: 0,
          num_items: selectedPerPage,
        };
        const pivotFilter = getPivotFilterForApi();
        if (pivotFilter) {
          payload.pivot_filter = pivotFilter;
        }

        const res = await apiRequest(
          "dashboards/root-cause",
          "POST",
          payload,
          true
        );
        if (res?.success && res.success.data && res.success.data.length > 0) {
          setData(res.success.data);
          setNumItems(res.success.pagination?.total || res.success.data.length);
          setPaginationInfo(res.success.pagination);
          setPageState("has_data");
          return;
        }
      }

      // No job and no insights - show empty state
      setPageState("empty");
    } catch (error) {
      console.error("Error determining initial state:", error);
      if (isMountedRef.current) {
        // ADD THIS IF CHECK
        setPageState("empty");
      }
    } finally {
      if (isMountedRef.current) {
        // ADD THIS IF CHECK
        setLoading(false);
      }
    }
  };
  
  // Define columns without copy buttons
  const columns = [
    {
      title: "ID",
      dataIndex: "ID",
      key: "rc_id",
      width: 60,
      sorter: (a, b) => a.rc_id - b.rc_id,
      render: (text, record) => <span>{record.rc_id}</span>,
    },
    {
      title: "Date Period",
      dataIndex: "date_period",
      key: "date_period",
      width: 140,
      sorter: (a, b) => (a.date_period || "").localeCompare(b.date_period || ""),
      render: (text) => <span>{formatDatePeriod(text)}</span>,
    },
    {
      title: "Pivot Filter",
      dataIndex: "pivot",
      key: "pivot",
      width: 140,
      sorter: (a, b) => (a.pivot || "").localeCompare(b.pivot || ""),
      render: (text) => <span>{text || "—"}</span>,
    },
    {
      title: "Area",
      dataIndex: "label",
      key: "label",
      width: 120,
      sorter: (a, b) => {
        const labelA = a.label || "No Topic";
        const labelB = b.label || "No Topic";
        return labelA.localeCompare(labelB);
      },
      render: (text, record) => {
        // Handle empty or null labels
        if (!text || text.trim() === "") {
          return (
            <span
              style={{
                color: theme === "dark" ? "#8c8c8c" : "#999",
                fontStyle: "italic",
              }}
            ></span>
          );
        }

        const displayText = text.replace(/^.\s/, "");
        const emoji = text.match(/^.\s/) ? text.split(" ")[0] : "";

        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              minWidth: 0,
            }}
          >
            <span
              style={{
                flex: 1,
                minWidth: 0,
                wordBreak: "break-word",
                whiteSpace: "normal",
              }}
            >
              <span className="areaEmoji">{emoji}</span>
              {displayText}
            </span>
          </div>
        );
      },
    },
    {
      title: "Root Cause",
      dataIndex: "root_cause",
      key: "root_cause",
      width: 180,
      sorter: (a, b) => a.root_cause.localeCompare(b.root_cause),
      render: (text, record) => {
        return (
          <div
            style={{
              cursor: "pointer",
              padding: "4px",
              borderRadius: "4px",
              transition: "text-decoration 0.2s",
              position: "relative",
              minWidth: 0,
            }}
            onMouseEnter={(e) => {
              e.target.style.textDecoration = "underline";
            }}
            onMouseLeave={(e) => {
              e.target.style.textDecoration = "none";
            }}
            onClick={() => fetchReviewsForIssue(record)}
          >
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "4px",
                minWidth: 0,
              }}
            >
              <span
                style={{
                  flex: 1,
                  minWidth: 0,
                  wordBreak: "break-word",
                  whiteSpace: "normal",
                }}
              >
                {text}
                <ArrowUpOutlined
                  style={{
                    color: theme === "dark" ? "#8c8c8c" : "#666",
                    fontSize: "12px",
                    marginLeft: "4px",
                    transform: "rotate(45deg)",
                  }}
                />
              </span>
            </div>
          </div>
        );
      },
    },
    {
      title: "Recommendation",
      dataIndex: "recommendation",
      key: "recommendation",
      width: 200,
      sorter: (a, b) => a.recommendation.localeCompare(b.recommendation),
      render: (text, record) => {
        return (
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: "4px",
              minWidth: 0,
            }}
          >
            <span
              style={{
                flex: 1,
                minWidth: 0,
                wordBreak: "break-word",
                whiteSpace: "normal",
              }}
            >
              {text}
            </span>
          </div>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      sorter: (a, b) => {
        if (a.status === b.status) return 0;
        return a.status ? 1 : -1;
      },
      width: 120,
      render: (status, record) => (
        <StatusUpdateComponent
          record={record}
          onStatusUpdate={() => fetchData(urlStartIndex)}
        />
      ),
    },
    {
      title: "Updated By",
      key: "updated_by",
      width: 132,
      ellipsis: true,
      sorter: (a, b) => (a.updated_by || 0) - (b.updated_by || 0),
      render: (_, record) => {
        return (
          <span
            style={{
              fontSize: "12px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              display: "inline-block",
              maxWidth: "120px",
            }}
          >
            {record?.updated_by || ""}
          </span>
        );
      },
    },
  ];

  useEffect(() => {
    let isMounted = true;
    const dashboardId = parseInt(params.id);

    // Fetch dashboard info and set in Redux (only once)
    if (dashboardId && !dashboardLoaded) {
      apiRequest(
        "dashboards/info",
        "POST",
        { dashboard_id: dashboardId },
        true
      ).then((result) => {
        if (result && result.success && isMounted) {
          dispatch({
            type: actions.SET_Dashboard,
            payload: { dashboard: result.success },
          });
          setDashboardLoaded(true);
        }
      });
    }

    return () => {
      isMounted = false;
    };
  }, [params.id, dispatch, dashboardLoaded]);

  // Initial load effect
  useEffect(() => {
    if (params.id && dashboardLoaded) {
      determineInitialState();
    }
  }, [params.id, dashboardLoaded]);

  // Fetch pivot options when we have insights data
  useEffect(() => {
    if (params.id) {
      fetchPivotOptions();
      fetchGenerationPivotOptions();
    }
  }, [params.id]);

  // Fetch pivot options when we have insights data
  useEffect(() => {
    if (pageState === "has_data" && params.id) {
      fetchPivotOptions();
    }
  }, [pageState, params.id]);

  // Separate useEffect for handling URL params and data fetching
  useEffect(() => {
    if (pageState === "has_data") {
      // Only update if different from current startIndex
      if (urlStartIndex !== startIndex) {
        setStartIndex(urlStartIndex);
        setPageNumber(urlStartIndex + 1);
        fetchData(urlStartIndex);
      }
    }
  }, [location.search, pageState]);

  // Render different states
  const renderContent = () => {
    const currentPivotFilter = getPivotFilterForApi();
    const inlineJobDetails = formatInlineJobDetails({
      since: searchParams.get("since"),
      until: searchParams.get("until"),
      pivotFilter: currentPivotFilter,
    });

    if (loading && pageState === "loading") {
      return (
        <div className={styles.noData}>
          <Spin size="large" />
          <p style={{ marginTop: "16px", textAlign: "center" }}>
            Loading AI Insights...
          </p>
        </div>
      );
    }

    if (pageState === "job_running" && jobStatus) {
      return (
        <JobStatusCard
          jobStatus={jobStatus}
          onRefresh={refreshJobStatus}
          onCancel={cancelJob}
        />
      );
    }

    if (pageState === "empty") {
      return (
        <EmptyTopInsights
          onGenerate={() => setShowGenerationModal(true)}
          isGenerating={pageState === "job_running"}
          generationProgress={jobStatus?.progress || 0}
          estimatedTimeRemaining={jobStatus?.estimated_completion}
          onConnectData={() => {
            /* handle data connection */
          }}
          onLearnMore={() => {
            /* handle learn more */
          }}
        />
      );
    }

    if (pageState === "has_data" && data.length > 0) {
      return (
        <>
          {jobStatus?.status === 0 && (
            <JobStatusCard
              jobStatus={jobStatus}
              onRefresh={refreshJobStatus}
              onCancel={cancelJob}
              compact
              description="A new AI insights generation job is currently running for this dashboard."
              detailText={inlineJobDetails}
            />
          )}
          {/* Pivot toolbar + table: Affix below main navbar; Table sticky header for row scroll */}
          <Affix offsetTop={DASHBOARD_TOP_NAV_OFFSET}>
            <div className={styles.aiInsightsAffixInner}>
              <div className={styles.actionBar}>
                <div className={styles.dropdownFilter}>
                  {multiLevelOptions &&
                  Object.keys(multiLevelOptions?.level1 || {}).length > 0 ? (
                    <MLDropdown
                      title={
                        <span className={styles.pivotFilters}>
                          {t("selected_dashboard_page.138")} <DownOutlined />
                        </span>
                      }
                      level1Pos="right"
                      subLevelPos="right-top"
                      levelWiseOptions={multiLevelOptions}
                      levelWiseSelections={levelWisePivotSelections}
                      setLevelWiseSelections={setLevelWisePivotSelections}
                      reset={handleResetPivotFilters}
                      apply={handleApplyPivotFilters}
                      disabled={loading}
                      selectedFilterType={selectedPivotsOperator}
                      setSelectedFilterType={setSelectedPivotsOperator}
                    />
                  ) : (
                    <span className={styles.pivotFiltersDisabled}>
                      {t("selected_dashboard_page.138")} <DownOutlined />
                    </span>
                  )}
                </div>
                <div className={styles.rightActions}>
                  <Button
                    icon={<DownloadOutlined />}
                    onClick={handleDownloadAIInsights}
                    className={styles.secondaryAction}
                    loading={downloadInsightsLoading}
                    disabled={loading}
                  >
                    Download AI Insights
                  </Button>
                  <Button
                    icon={<RocketOutlined />}
                    onClick={handleRegenerate}
                    className={styles.primaryAction}
                  >
                    Generate AI Insights
                  </Button>
                </div>
              </div>
              <Table
                dataSource={data}
                columns={columns}
                loading={loading}
                rowKey={(record) => record.rc_id}
                pagination={false}
                sticky
                scroll={{
                  x: 1220,
                  y: AI_INSIGHTS_TABLE_BODY_MAX_HEIGHT,
                }}
              />
            </div>
          </Affix>

          {/* Custom Pagination */}
          <div className={styles.paginationWrapper}>
            <div className={styles.innerWrapper}>
              {data?.length > 0 && (
                <div className={styles.paginationBox}>
                  <Button
                    disabled={
                      paginationInfo
                        ? !paginationInfo.has_prev
                        : startIndex === 0
                    }
                    onClick={() => {
                      const newStartIndex = paginationInfo
                        ? paginationInfo.prev_start_index || 0
                        : Math.max(0, startIndex - 1);
                      updateQParams("start_index", newStartIndex);
                    }}
                  >
                    <i className="fa fa-angle-left" />
                  </Button>

                  <span className={styles.pagination}>
                    {paginationInfo
                      ? `${paginationInfo.current_page} / ${paginationInfo.total_pages}`
                      : numItems > 0
                      ? `${pageNumber} / ${Math.ceil(
                          numItems / selectedPerPage
                        )}`
                      : `${pageNumber}`}
                  </span>

                  <Button
                    disabled={
                      paginationInfo
                        ? !paginationInfo.has_next
                        : data.length < selectedPerPage ||
                          (numItems > 0 &&
                            startIndex + 1 >=
                              Math.ceil(numItems / selectedPerPage))
                    }
                    onClick={() => {
                      const newStartIndex = paginationInfo
                        ? paginationInfo.next_start_index
                        : startIndex + 1;
                      updateQParams("start_index", newStartIndex);
                    }}
                  >
                    <i className="fa fa-angle-right" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        </>
      );
    }

    // Fallback to empty state
    return <EmptyTopInsights onGenerate={() => setShowGenerationModal(true)} />;
  };

  return (
    <div className={styles.mainWindow}>
      <DashboardPageChrome>
        <DashboardHeader
          {...props}
          topicsType="ai-insights"
          dashboardId={params.id}
        />
        <NestedTabs />
      </DashboardPageChrome>
      <div className={styles.tableWidget}>{renderContent()}</div>

      {/* AI Insights Generation Modal */}
      <AIInsightsModal
        visible={showGenerationModal}
        onClose={() => setShowGenerationModal(false)}
        onSubmit={startGeneration}
        topics={topics}
        loading={generationLoading}
        dashboardId={parseInt(params.id)}
        pivotFilter={getPivotFilterForApi()}
        pivotOptions={generationPivotOptions || pivotOptions}
        isDark={theme === "dark"}
      />

      {/* Reviews Drawer */}
      <Drawer
        title={
          selectedIssue ? (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "4px" }}
            >
              <span style={{ fontWeight: "bold" }}>{selectedIssue.label}</span>
              <span
                style={{
                  fontSize: "12px",
                  color: theme === "dark" ? "#8c8c8c" : "#666",
                }}
              >
                {selectedIssue.root_cause}
              </span>
            </div>
          ) : (
            "Reviews"
          )
        }
        placement="right"
        closable={true}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedIssue(null);
          setIssueReviews([]);
        }}
        open={drawerOpen}
        width={
          window.innerWidth > 1300 ? 650 : window.innerWidth > 500 ? 450 : 350
        }
      >
        <Spin spinning={reviewsLoading}>
          <WelcomeDrawerReviewsList
            allReviews={issueReviews}
            setReviewsLoading={setReviewsLoading}
          />
        </Spin>
      </Drawer>
    </div>
  );
};

export default TopProblems;